import React, { useState } from 'react';
import { Alert, Button, message } from 'antd';
import {
	useCompleteProgramMutation,
	useGetProgramQuery,
	useStartProgramMutation,
} from '../../../redux/api/manufacturing';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';
import {
	PRODUCTION_QUESTION_BLOCK_HINT,
	getProductionQuestionBlockMessage,
} from '../../../utils/productionQuestionBlock';
import { useProgramImages } from '../useProgramImages';
import EmbedHeader from './EmbedHeader';
import EmbedProgrammingFeedback from './EmbedProgrammingFeedback';
import ProgrammingClock from './ProgrammingClock';

import './embed.css';

interface EmbedProgrammingPageProps {
	/** Known program id from a prior lookup. */
	programId?: string;
	/** FASTLANE folder names when the program may not exist yet. */
	orderName?: string;
	partName?: string;
}

const isActiveSession = (program: {
	programming_started_at?: string | null;
	programming_completed_at?: string | null;
}) =>
	!!program.programming_started_at && !program.programming_completed_at;

/**
 * Minimal programming surface for embedding in Fusion 360. The programmer
 * starts the clock, keeps notes along the way, and ends the session when done.
 */
const EmbedProgrammingPage: React.FC<EmbedProgrammingPageProps> = ({
	programId,
	orderName,
	partName,
}) => {
	const {
		data: fetchedProgram,
		isLoading: isLoadingProgram,
		isError: isProgramError,
	} = useGetProgramQuery(programId ?? '', { skip: !programId });

	const [startProgram, { isLoading: isStarting, data: startedProgram }] =
		useStartProgramMutation();
	const [completeProgram, { isLoading: isEnding }] =
		useCompleteProgramMutation();
	const [questionBlock, setQuestionBlock] = useState<string | null>(null);

	const program = startedProgram ?? fetchedProgram ?? null;
	const images = useProgramImages(program?.id ?? '');

	const handleStart = async () => {
		try {
			await startProgram({
				program: programId,
				order_name: orderName?.trim() || undefined,
				part_name: partName?.trim() || undefined,
				program_type: 'cam',
			}).unwrap();
		} catch {
			message.error('Could not start programming');
		}
	};

	const handleEnd = async () => {
		if (!program) return;

		setQuestionBlock(null);
		try {
			await completeProgram({ id: program.id }).unwrap();
			message.success('Programming ended');
		} catch (error) {
			const block = getProductionQuestionBlockMessage(error);
			if (block) {
				setQuestionBlock(block);
				return;
			}
			message.error(getApiErrorMessage(error, 'Could not end programming'));
		}
	};

	const lineItem = program?.order_line_item ?? null;
	const title = lineItem?.name || partName?.trim() || program?.filename || 'Program';
	const subtitle =
		[lineItem?.order_name || orderName, program?.filename]
			.filter((part): part is string => !!part && part !== title)
			.join(' \u00b7 ') || null;

	const body = () => {
		if (programId && (isProgramError || (!isLoadingProgram && !program))) {
			return (
				<div className="embed-program-review__error">
					Unable to load this program
				</div>
			);
		}

		if (isLoadingProgram && !program) {
			return (
				<div className="embed-program-review__loading">Loading program…</div>
			);
		}

		if (!program || !isActiveSession(program)) {
			if (program?.programming_completed_at) {
				return (
					<p className="embed-review-start__hint">
						This program was already logged. You can still edit the notes below.
					</p>
				);
			}

			return (
				<div className="embed-review-start">
					<p className="embed-review-start__hint">
						Start the clock when you begin programming this part in Fusion.
					</p>
					<Button
						type="primary"
						block
						loading={isStarting}
						onClick={handleStart}
					>
						Start Programming
					</Button>
				</div>
			);
		}

		return null;
	};

	const sessionActive = program ? isActiveSession(program) : false;

	return (
		<div className="embed-program-review">
			<EmbedHeader title={title} subtitle={subtitle} mode="programming">
				{program && (sessionActive || program.programming_completed_at) ? (
					<ProgrammingClock
						program={program}
						onEnd={handleEnd}
						isEnding={isEnding}
					/>
				) : null}
			</EmbedHeader>
			{questionBlock && (
				<Alert
					type="warning"
					showIcon
					closable
					onClose={() => setQuestionBlock(null)}
					message={questionBlock}
					description={PRODUCTION_QUESTION_BLOCK_HINT}
					style={{ margin: '12px 0' }}
				/>
			)}
			{body()}
			{program ? (
				<EmbedProgrammingFeedback program={program} images={images} />
			) : null}
		</div>
	);
};

export default EmbedProgrammingPage;
